import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

import { initialState } from "@/context/Audience/types";
import { MetaInterest } from "@/types";




interface ActivationState {
  selection: MetaInterest[][];
  setSelection: (recipe: MetaInterest[][]) => void;
  activeSelection: number;
  setActiveSelection: (id: number) => void;
  addSelection: () => void;
  removeSelection: (idx: number) => void;
  selectInterest: (interest: MetaInterest, listId: number) => void;
  selectMultiInterest: (interests: MetaInterest[], listId: number) => void;
  removeInterest: (interest: MetaInterest, listId: number) => void;
}


export const ActivationContext = createContext<ActivationState | undefined>(undefined);

export const ActivationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selection, setSelection] = useState<MetaInterest[][]>([]);
  const [activeSelection, setActiveSelection] = useState<number>(0);

  const addSelection = () => {
    setSelection(prev => [...prev, []]);
    setActiveSelection(prev => prev + 1);
  }

  const removeSelection = (idx: number) => {
    setSelection(prev => prev.filter((_, i) => i !== idx));
    setActiveSelection(prev => prev - 1);
  }

  const updateSelection = (interest: MetaInterest, interests: MetaInterest[]) => {
    if (interests.map(x => x.meta_id).includes(interest.meta_id)) {
      return interests.filter(x => x.meta_id !== interest.meta_id)
    } else {
      return [...interests, interest];
    }
  }

  const selectInterest = useCallback((interest: MetaInterest, listId: number) => {
    setSelection(prev => {
      const target = prev[listId];
      const update = updateSelection(interest, target);
      return prev.map((list, i) => (i === listId ? update : list));
    })
  }, []);

  const selectMultiInterest = (interests: MetaInterest[], listId: number) => {
    setSelection(prev => {
      const existing = prev[listId];
      const itemIds = new Set(existing.map(x => x.meta_id));
      const newItems = interests.filter(x => !itemIds.has(x.meta_id));
      const update = [...existing, ...newItems];

      const next = prev.slice();
      next[listId] = update;
      return next;
    })
  }

  const removeInterest = (interest: MetaInterest, listId: number) => {
    setSelection(prev => {
      const update = prev[listId].filter(x => x.meta_id !== interest.meta_id);
      return prev.map((list, i) => (i === listId ? update : list))
    })
  }

  const value = useMemo<ActivationState>(() => ({
    selection,
    setSelection,
    activeSelection,
    setActiveSelection,
    addSelection,
    removeSelection,
    selectInterest,
    selectMultiInterest,
    removeInterest,
  }), [selection, activeSelection, selectInterest])

  return (
    <ActivationContext.Provider value={value}>
      {children}
    </ActivationContext.Provider>
  );
};

export const useActivationContext = () => {
  const context = useContext(ActivationContext);
  if (!context) {
    throw new Error(
      "useActivationContext must be used within an ActivationProvider",
    );
  }
  return context;
};

